import React, { useCallback, useMemo, useState } from 'react';
import { useSound } from '../../utils/sound/SoundProvider';
import useGameBase from '../../hooks/useGameBase';

const FALLBACK_COLORS = ['#f97316', '#06b6d4', '#84cc16', '#f43f5e', '#8b5cf6', '#eab308', '#14b8a6', '#ef4444'];
const LADDER_COLORS = ['#f59e0b', '#06b6d4', '#eab308', '#84cc16', '#f43f5e', '#8b5cf6', '#14b8a6', '#ef4444'];
const SNAKE_COLORS = ['#22c55e', '#ef4444', '#0ea5e9', '#a21caf', '#f43f5e', '#eab308', '#8b5cf6', '#f97316'];

function buildBoardRows(boardSize, columns, rows) {
  const renderedRows = [];
  for (let actualRow = rows - 1; actualRow >= 0; actualRow--) {
    const rowStart = actualRow * columns + 1;
    const rowEnd = Math.min(boardSize, rowStart + columns - 1);
    const values = [];
    for (let value = rowStart; value <= rowEnd; value++) {
      values.push(value);
    }

    const remaining = columns - values.length;
    const isLeftToRight = actualRow % 2 === 0;
    const padded = isLeftToRight
      ? [...values, ...Array.from({ length: remaining }, () => null)]
      : [...Array.from({ length: remaining }, () => null), ...values.reverse()];

    renderedRows.push(padded);
  }
  return renderedRows;
}

function getPlayerColor(player, index) {
  return player?.color || FALLBACK_COLORS[index % FALLBACK_COLORS.length];
}

function getPlayerLabel(player, index) {
  if (player?.icon) return player.icon;
  const initials = (player?.username || `P${index + 1}`)
    .split(' ')
    .filter(Boolean)
    .map((part) => part[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
  return initials || `P${index + 1}`;
}

function DiceFace({ value, rolling, onClick, canRoll }) {
  const pipsByValue = {
    1: [4],
    2: [0, 8],
    3: [0, 4, 8],
    4: [0, 2, 6, 8],
    5: [0, 2, 4, 6, 8],
    6: [0, 2, 3, 5, 6, 8],
  };
  const pips = pipsByValue[value] || pipsByValue[1];

  return (
    <button
      type={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : -1}
      onClick={canRoll ? onClick : undefined}
      disabled={!canRoll}
      aria-label={canRoll ? 'Roll Dice' : 'Dice'}
      className={`inline-grid h-14 w-14 grid-cols-3 grid-rows-3 place-items-center rounded-lg border-2 border-slate-400 bg-white p-1 dark:border-white/30 dark:bg-white/20 focus:outline-none transition-transform ${rolling ? 'animate-pulse' : ''} ${canRoll ? 'snl-dice-glow' : ''}`}
      style={{ cursor: canRoll ? 'pointer' : 'default' }}
    >
      {Array.from({ length: 9 }, (_, index) => (
        <span
          key={`pip-${index}`}
          className={`h-3 w-3 sm:h-3.5 sm:w-3.5 md:h-4 md:w-4 rounded-full ${pips.includes(index) ? 'bg-slate-800 dark:bg-white' : 'bg-transparent'}`}
          style={{ height: 12, width: 12, minWidth: 12, minHeight: 12, maxWidth: 16, maxHeight: 16 }}
        />
      ))}
    </button>
  );
}

function buildSquarePositionMap(boardRows) {
  const map = new Map();
  boardRows.forEach((row, rowIndex) => {
    row.forEach((square, columnIndex) => {
      if (square) {
        map.set(square, { row: rowIndex, column: columnIndex });
      }
    });
  });
  return map;
}

function getSquareCenter(square, squarePositions) {
  const position = squarePositions.get(square);
  if (!position) return null;
  return {
    x: position.column * 100 + 50,
    y: position.row * 100 + 50,
  };
}

function buildLadderPathPoints(from, to) {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const distance = Math.sqrt(dx * dx + dy * dy) || 1;
  const nx = dx / distance;
  const ny = dy / distance;
  const inset = 18;

  return [
    from,
    { x: from.x + nx * inset, y: from.y + ny * inset },
    { x: to.x - nx * inset, y: to.y - ny * inset },
    to,
  ];
}

function buildSnakePathPoints(from, to) {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const distance = Math.sqrt(dx * dx + dy * dy) || 1;
  const px = -(dy / distance);
  const py = dx / distance;
  const amplitude = Math.max(26, Math.min(46, distance * 0.12));
  const segments = Math.max(3, Math.min(5, Math.floor(distance / 120)));
  const points = [{ x: from.x, y: from.y }];

  for (let index = 1; index <= segments; index++) {
    const t = index / (segments + 1);
    const wave = index % 2 === 0 ? -1 : 1;
    points.push({
      x: from.x + dx * t + px * amplitude * wave,
      y: from.y + dy * t + py * amplitude * wave,
    });
  }

  points.push({ x: to.x, y: to.y });
  return points;
}

function buildJumpPathPoints(jump, squarePositions) {
  if (!jump) return [];
  const from = getSquareCenter(jump.start, squarePositions);
  const to = getSquareCenter(jump.end, squarePositions);
  if (!from || !to) return [];
  return jump.type === 'ladder'
    ? buildLadderPathPoints(from, to)
    : buildSnakePathPoints(from, to);
}

function interpolatePathPoints(points, stepsPerSegment = 10) {
  if (!Array.isArray(points) || points.length < 2) return [];
  const frames = [];

  for (let index = 0; index < points.length - 1; index++) {
    const start = points[index];
    const end = points[index + 1];
    for (let step = 0; step < stepsPerSegment; step++) {
      const t = step / stepsPerSegment;
      frames.push({
        x: start.x + (end.x - start.x) * t,
        y: start.y + (end.y - start.y) * t,
      });
    }
  }

  frames.push(points[points.length - 1]);
  return frames;
}

function LadderGraphic({ from, to, color = '#f59e0b' }) {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const distance = Math.sqrt(dx * dx + dy * dy) || 1;
  const nx = dx / distance;
  const ny = dy / distance;
  const px = -ny;
  const py = nx;
  const railOffset = 16;
  const [, startPoint, endPoint] = buildLadderPathPoints(from, to);
  const startX = startPoint.x;
  const startY = startPoint.y;
  const endX = endPoint.x;
  const endY = endPoint.y;
  const leftStart = { x: startX + px * railOffset, y: startY + py * railOffset };
  const leftEnd = { x: endX + px * railOffset, y: endY + py * railOffset };
  const rightStart = { x: startX - px * railOffset, y: startY - py * railOffset };
  const rightEnd = { x: endX - px * railOffset, y: endY - py * railOffset };
  const rungCount = Math.max(3, Math.min(7, Math.floor(distance / 90)));
  const rungs = Array.from({ length: rungCount }, (_, index) => {
    const t = (index + 1) / (rungCount + 1);
    return {
      left: {
        x: leftStart.x + (leftEnd.x - leftStart.x) * t,
        y: leftStart.y + (leftEnd.y - leftStart.y) * t,
      },
      right: {
        x: rightStart.x + (rightEnd.x - rightStart.x) * t,
        y: rightStart.y + (rightEnd.y - rightStart.y) * t,
      },
    };
  });

  return (
    <g opacity="0.93">
      <line x1={leftStart.x} y1={leftStart.y} x2={leftEnd.x} y2={leftEnd.y} stroke="#1f2937" strokeWidth="12" strokeLinecap="round" />
      <line x1={rightStart.x} y1={rightStart.y} x2={rightEnd.x} y2={rightEnd.y} stroke="#1f2937" strokeWidth="12" strokeLinecap="round" />
      <line x1={leftStart.x} y1={leftStart.y} x2={leftEnd.x} y2={leftEnd.y} stroke={color} strokeWidth="7" strokeLinecap="round" />
      <line x1={rightStart.x} y1={rightStart.y} x2={rightEnd.x} y2={rightEnd.y} stroke={color} strokeWidth="7" strokeLinecap="round" />
      {rungs.map((rung, index) => (
        <line
          key={`rung-${index}`}
          x1={rung.left.x}
          y1={rung.left.y}
          x2={rung.right.x}
          y2={rung.right.y}
          stroke="#111827"
          strokeWidth="7"
          strokeLinecap="round"
        />
      ))}
    </g>
  );
}

function SnakeGraphic({ from, to, color = '#22c55e' }) {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const distance = Math.sqrt(dx * dx + dy * dy) || 1;
  const nx = dx / distance;
  const ny = dy / distance;
  const px = -ny;
  const py = nx;
  const points = buildSnakePathPoints(from, to);

  const path = points
    .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`)
    .join(' ');

  const headBaseX = from.x + nx * 18;
  const headBaseY = from.y + ny * 18;
  const headLeftX = headBaseX + px * 18;
  const headLeftY = headBaseY + py * 18;
  const headRightX = headBaseX - px * 18;
  const headRightY = headBaseY - py * 18;
  const stripePath = points
    .slice(1, -1)
    .map((point, index) => {
      const localT = (index + 1) / (points.length - 1);
      const wave = index % 2 === 0 ? 1 : -1;
      const sx = point.x + px * wave * 8;
      const sy = point.y + py * wave * 8;
      const ex = point.x - px * wave * 8;
      const ey = point.y - py * wave * 8;
      return { key: `${localT}-${index}`, sx, sy, ex, ey };
    });

  return (
    <g opacity="0.93">
      <path d={path} fill="none" stroke="#0f172a" strokeWidth="26" strokeLinecap="round" strokeLinejoin="round" />
      <path d={path} fill="none" stroke={color} strokeWidth="20" strokeLinecap="round" strokeLinejoin="round" />
      <path d={path} fill="none" stroke="#fef08a" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="8 26" strokeDashoffset="6" opacity="0.9" />
      {stripePath.map((stripe) => (
        <line
          key={stripe.key}
          x1={stripe.sx}
          y1={stripe.sy}
          x2={stripe.ex}
          y2={stripe.ey}
          stroke="#14532d"
          strokeWidth="4"
          strokeLinecap="round"
          opacity="0.85"
        />
      ))}
      <circle cx={from.x} cy={from.y} r="20" fill={color} stroke="#14532d" strokeWidth="6" />
      <circle cx={from.x - px * 6 + nx * 2} cy={from.y - py * 6 + ny * 2} r="4" fill="#f8fafc" />
      <circle cx={from.x + px * 6 + nx * 2} cy={from.y + py * 6 + ny * 2} r="4" fill="#f8fafc" />
      <circle cx={from.x - px * 6 + nx * 2} cy={from.y - py * 6 + ny * 2} r="2" fill="#111827" />
      <circle cx={from.x + px * 6 + nx * 2} cy={from.y + py * 6 + ny * 2} r="2" fill="#111827" />
      <path d={`M ${from.x} ${from.y + 7} Q ${from.x + px * 8} ${from.y + py * 8} ${from.x + nx * 18} ${from.y + ny * 18}`} stroke="#ef4444" strokeWidth="4" fill="none" strokeLinecap="round" />
      <path d={`M ${from.x + nx * 18} ${from.y + ny * 18} L ${from.x + nx * 25 + px * 4} ${from.y + ny * 25 + py * 4} M ${from.x + nx * 18} ${from.y + ny * 18} L ${from.x + nx * 25 - px * 4} ${from.y + ny * 25 - py * 4}`} stroke="#ef4444" strokeWidth="2" strokeLinecap="round" />
      <polygon points={`${from.x},${from.y} ${headLeftX},${headLeftY} ${headRightX},${headRightY}`} fill="#166534" opacity="0.5" />
    </g>
  );
}

function SnakesAndLaddersGame({ gameState = {}, gamePlayers = [], user = {}, socket, roomId }) {
    const [overshootMessage, setOvershootMessage] = useState('');
  const sound = useSound();
  const [buttonHidden, setButtonHidden] = useState(false);
      // Use a local animation trigger to ensure dice always animates
      const [diceAnimationTrigger, setDiceAnimationTrigger] = useState(0);
    // Track if local player has already rolled this turn
    const [hasRolled, setHasRolled] = useState(false);
  // State for dice and counter animation
  const [isRolling, setIsRolling] = useState(false);
  const [rollingFace, setRollingFace] = useState(1);
  const [isAnimatingMove, setIsAnimatingMove] = useState(false);
  const [heldCounter, setHeldCounter] = useState(null); // { playerId, square?, x?, y? }

  const { submitAction, isLocalPlayerTurn, findPlayerById } = useGameBase({
    gameState,
    gamePlayers,
    user,
    socket,
    roomId,
  });

  const boardSize = Number(gameState.boardSize) || 100;
  const columns = Number(gameState.columns) || Math.max(5, Math.ceil(Math.sqrt(boardSize)));
  const rows = Number(gameState.rows) || Math.ceil(boardSize / columns);
  const boardRows = useMemo(() => buildBoardRows(boardSize, columns, rows), [boardSize, columns, rows]);
  const jumps = useMemo(() => (Array.isArray(gameState.jumps) ? gameState.jumps : []), [gameState.jumps]);
  const squarePositions = useMemo(() => buildSquarePositionMap(boardRows), [boardRows]);
  const positions = useMemo(() => (gameState.positions || {}), [gameState.positions]);
  const scores = gameState.scores || {};
  const isMyTurn = isLocalPlayerTurn();

  // Reset hasRolled when the turn changes
  // Reset hasRolled when the turn changes or when the board updates after move-complete
  React.useEffect(() => {
    setHasRolled(false);
  }, [gameState.currentPlayer, gameState.positions, gameState.lastMove]);

  // During animation, show heldCounter position for moving player
  const displayPositions = React.useMemo(() => {
    if (heldCounter && typeof heldCounter.x === 'number' && typeof heldCounter.y === 'number') {
      const nextPositions = { ...positions };
      delete nextPositions[heldCounter.playerId];
      return nextPositions;
    }
    if (heldCounter && typeof heldCounter.square === 'number') {
      return {
        ...positions,
        [heldCounter.playerId]: heldCounter.square,
      };
    }
    return positions;
  }, [positions, heldCounter]);

  const highlightedSet = useMemo(() => new Set([]), []);

  const playersOnBoardSquare = useMemo(() => {
    const acc = {};
    gamePlayers.forEach((player) => {
      const rawSquare = Number(displayPositions[player.socketId] || 0);
      if (rawSquare <= 0) return;
      const square = Math.min(boardSize, rawSquare);
      if (!acc[square]) acc[square] = [];
      acc[square].push(player.socketId);
    });
    return acc;
  }, [gamePlayers, displayPositions, boardSize]);

  const animateCounterMove = useCallback(async (move, roll, shouldSendMoveComplete) => {
    if (!move || typeof move.player === 'undefined' || move.player === null) return;
    setIsAnimatingMove(true);
    const playerId = move.player;
    const start = Number(move.start || 0);
    const landed = Number(move.landed || start);
    const end = Number(move.end || landed);
    const overshot = Boolean(move.overshot);
    const travelSquares = [];
    if (landed > start) {
      for (let square = start + 1; square <= landed; square++) travelSquares.push(square);
    }
    const jump = end !== landed
      ? (Array.isArray(gameState.jumps) ? gameState.jumps : []).find((entry) => entry.start === landed && entry.end === end)
      : null;
    if (overshot) {
      if (sound && typeof sound.play === 'function') {
        sound.play('ui:negative');
      }
      setOvershootMessage('You need to finish with an exact roll of the dice!');
      setTimeout(() => setOvershootMessage(''), 2200);
      setHeldCounter({ playerId, square: start });
      await new Promise((resolve) => setTimeout(resolve, 900));
      setHeldCounter(null);
      setIsAnimatingMove(false);
      if (shouldSendMoveComplete) {
        await submitAction({
          type: 'move-complete',
          start,
          landed,
          end,
          jumpType: null,
          jumpStart: null,
          jumpEnd: null,
        });
      }
      return;
    }
    for (let i = 0; i < travelSquares.length; i++) {
      setHeldCounter({ playerId, square: travelSquares[i] });
      await new Promise((resolve) => setTimeout(resolve, 220));
    }
    if (jump) {
      // Play sound for ladder or snake
      if (sound && typeof sound.play === 'function') {
        if (jump.type === 'ladder') {
          sound.play('snakesandladders:ladder');
        } else if (jump.type === 'snake') {
          sound.play('snakesandladders:snake');
        }
      }
      const jumpFrames = interpolatePathPoints(
        buildJumpPathPoints(jump, squarePositions),
        jump.type === 'snake' ? 12 : 10
      );
      for (let i = 0; i < jumpFrames.length; i++) {
        setHeldCounter({ playerId, x: jumpFrames[i].x, y: jumpFrames[i].y });
        await new Promise((resolve) => setTimeout(resolve, jump.type === 'snake' ? 45 : 40));
      }
    }
    setHeldCounter({ playerId, square: end });
    setIsAnimatingMove(false);
    if (shouldSendMoveComplete) {
      const jump = (Array.isArray(gameState.jumps) ? gameState.jumps : []).find((j) => j.start === landed && j.end === end);
      await submitAction({
        type: 'move-complete',
        start,
        landed,
        end,
        jumpType: jump ? jump.type : null,
        jumpStart: jump ? jump.start : null,
        jumpEnd: jump ? jump.end : null,
      });
    }
  }, [gameState.jumps, squarePositions, submitAction, sound]);

  // When player clicks roll, immediately send to server (no animation yet)
  const handleRoll = () => {
    if (!isMyTurn || gameState.finished || isRolling || isAnimatingMove || hasRolled) return;
    setButtonHidden(true);
    setHasRolled(true);
    submitAction({ type: 'roll-dice' }).catch(() => {});
  };

  // Reset buttonHidden when the turn changes
  React.useEffect(() => {
    setButtonHidden(false);
  }, [gameState.currentPlayer]);

  // Play dice sound using SoundProvider when dice starts rolling
  React.useEffect(() => {
    if (isRolling && sound && typeof sound.play === 'function') {
      sound.play('ui:rolldie');
    }
  }, [isRolling, sound]);

  // Animate dice and counter when a new roll is received
  // Always animate dice when a new roll is received, even if the value is the same
  React.useEffect(() => {
    if (
      typeof gameState.lastRoll !== 'number' ||
      isRolling ||
      isAnimatingMove
    ) return;

    // All clients animate dice and move after a new lastRoll, but only once per roll.
    // During the roll phase the server has not advanced currentPlayer yet, so the
    // current player is the player whose token should move and who must send move-complete.
    const lastRollRef = window.__snl_lastRollRef = window.__snl_lastRollRef || { roll: null };
    const rollingPlayer = (() => {
      if (gameState.lastMove && typeof gameState.lastMove.player === 'string') return gameState.lastMove.player;
      if (typeof gameState.currentPlayer === 'string') return gameState.currentPlayer;
      return null;
    })();
    if (
      typeof gameState.lastRoll === 'number' &&
      !isRolling &&
      !isAnimatingMove &&
      lastRollRef.roll !== gameState.lastRoll &&
      !gameState.lastMove &&
      rollingPlayer
    ) {
      lastRollRef.roll = gameState.lastRoll;
      const playerId = rollingPlayer;
      const start = Number(positions[playerId] || 0);
      const attemptedLanding = start + Number(gameState.lastRoll);
      const overshot = attemptedLanding > boardSize;
      const landed = overshot ? start : attemptedLanding;
      let end = landed;
      const jump = landed > start
        ? (Array.isArray(gameState.jumps) ? gameState.jumps : []).find((j) => j.start === landed)
        : null;
      if (jump) end = jump.end;
      if (playerId != null && !isNaN(start) && !isNaN(landed) && !isNaN(end)) {
        // Only the player who rolled should send move-complete
        const isLocalPlayerWhoRolled = playerId === user.socketId;
        const onDiceAnimationEnd = () => {
          animateCounterMove({ player: playerId, start, landed, end, overshot }, gameState.lastRoll, isLocalPlayerWhoRolled);
        };
        window.__snl_onDiceAnimationEnd = onDiceAnimationEnd;
        setDiceAnimationTrigger((t) => t + 1);
      }
    }
    // Reset lastRollRef when move-complete is processed
    if (gameState.lastMove && lastRollRef.roll !== null) {
      lastRollRef.roll = null;
    }
  }, [animateCounterMove, gameState.lastRoll, gameState.lastMove, isRolling, isAnimatingMove, positions, gameState.currentPlayer, gameState.jumps, gameState.finished, user.socketId, gamePlayers, boardSize]);

  // Run dice animation whenever the trigger changes
  React.useEffect(() => {
    if (diceAnimationTrigger === 0) return;
    setIsRolling(true);
    // Support server-specified roll animation duration and dice value
    const rollDuration = typeof gameState.rollAnimationDuration === 'number' ? gameState.rollAnimationDuration : 900;
    const diceValue = typeof gameState.lastRoll === 'number' ? gameState.lastRoll : 1;
    let diceFace = 1;
    const diceInterval = setInterval(() => {
      diceFace = Math.floor(Math.random() * 6) + 1;
      setRollingFace(diceFace);
    }, 90);
    setTimeout(() => {
      clearInterval(diceInterval);
      setRollingFace(diceValue);
      setIsRolling(false);
      // Insert a short delay before starting counter animation
      setTimeout(() => {
        if (typeof window.__snl_onDiceAnimationEnd === 'function') {
          window.__snl_onDiceAnimationEnd();
          window.__snl_onDiceAnimationEnd = null;
        } else {
          // Fallback: animate lastMove if present (for server-driven moves)
          animateCounterMove(gameState.lastMove, diceValue);
        }
      }, 950); // 350ms delay between dice and counter
    }, rollDuration);
    // eslint-disable-next-line
  }, [diceAnimationTrigger]);

  // Clear heldCounter when the server updates the official position for that player
  React.useEffect(() => {
    if (!heldCounter) return;
    const { playerId, square } = heldCounter;
    if (positions && typeof positions[playerId] === 'number' && positions[playerId] === square) {
      setHeldCounter(null);
    }
  }, [positions, heldCounter]);

  const jumpGraphics = useMemo(() => {
    return jumps
      .map((jump, index) => {
        const from = getSquareCenter(jump.start, squarePositions);
        const to = getSquareCenter(jump.end, squarePositions);
        if (!from || !to) return null;
        return {
          ...jump,
          from,
          to,
          key: `${jump.type}-${jump.start}-${jump.end}-${index}`,
          jumpKey: `${jump.type}-${jump.start}-${jump.end}`,
        };
      })
      .filter(Boolean);
  }, [jumps, squarePositions]);

  const animatedCounterPlayer = heldCounter ? findPlayerById(heldCounter.playerId) : null;
  const animatedCounterIndex = heldCounter
    ? gamePlayers.findIndex((entry) => entry.socketId === heldCounter.playerId)
    : -1;
  const animatedCounterColor = heldCounter
    ? getPlayerColor(animatedCounterPlayer, animatedCounterIndex >= 0 ? animatedCounterIndex : 0)
    : null;
  const animatedCounterLabel = heldCounter
    ? getPlayerLabel(animatedCounterPlayer, animatedCounterIndex >= 0 ? animatedCounterIndex : 0)
    : null;

  return (
    <div className="mx-auto mt-4 flex w-full max-w-[110rem] flex-col gap-4 px-2 text-slate-800 dark:text-slate-100">
      <div
        style={{ display: overshootMessage ? 'block' : 'none' }}
        className="fixed left-1/2 top-8 z-[9999] -translate-x-1/2 rounded-xl bg-red-500 px-6 py-3 text-lg font-bold text-white shadow-lg animate-fade-in-out"
      >
        {overshootMessage}
      </div>
      <div className="overflow-hidden rounded-[2rem] border border-emerald-200 bg-gradient-to-br from-emerald-50 via-amber-50 to-sky-100 shadow-[0_20px_80px_rgba(15,23,42,0.12)] dark:border-slate-700 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
        <div className="flex flex-col gap-4 p-4 lg:p-5">
          <section className="rounded-[1.5rem] border border-white/70 bg-white/70 p-3 shadow-sm backdrop-blur dark:border-slate-700 dark:bg-slate-900/80 sm:p-4">
            {/* No jumpAnnouncement in minimal protocol */}
            <div className="relative overflow-hidden rounded-lg border-2 border-slate-300 bg-slate-200 shadow-inner dark:border-slate-600 dark:bg-slate-800">
              <svg
                className="pointer-events-none absolute inset-0 z-10"
                viewBox={`0 0 ${columns * 100} ${rows * 100}`}
                preserveAspectRatio="none"
                aria-hidden="true"
              >
                {jumpGraphics.map((jump, idx) => {
                  const color = jump.type === 'ladder'
                    ? LADDER_COLORS[idx % LADDER_COLORS.length]
                    : SNAKE_COLORS[idx % SNAKE_COLORS.length];
                  return (
                    <g
                      key={jump.key}
                    >
                      {jump.type === 'ladder'
                        ? <LadderGraphic from={jump.from} to={jump.to} color={color} />
                        : <SnakeGraphic from={jump.from} to={jump.to} color={color} />}
                    </g>
                  );
                })}
              </svg>

              <div
                className="relative z-30 grid gap-0"
                style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`, background: 'rgba(255,255,255,0.45)' }}
              >
                {boardRows.map((row, rowIndex) =>
                  row.map((square, columnIndex) => {
                    const cellKey = square || `empty-${rowIndex}-${columnIndex}`;
                    const isDarkCell = (rowIndex + columnIndex) % 2 === 1;

                    if (!square) {
                      return (
                        <div
                          key={cellKey}
                          className={`aspect-square border border-slate-300/70 dark:border-slate-700 ${
                            isDarkCell ? 'bg-slate-300/30 dark:bg-slate-700/30' : 'bg-slate-100/40 dark:bg-slate-900/30'
                          }`}
                        />
                      );
                    }

                    const squarePlayers = playersOnBoardSquare[square] || [];
                    const isFinal = square === boardSize;
                    const isHighlighted = highlightedSet.has(square);

                    return (
                      <div
                        key={cellKey}
                        className={`group relative aspect-square overflow-hidden border p-2 transition-transform hover:z-30 hover:scale-[1.01] sm:p-3 ${
                          isFinal
                            ? 'border-amber-500 bg-amber-300/80 dark:border-amber-400 dark:bg-amber-500/60'
                            : isDarkCell
                              ? 'border-slate-300/60 bg-slate-300/30 dark:border-slate-700 dark:bg-slate-700/30'
                              : 'border-slate-200/60 bg-slate-100/30 dark:border-slate-600 dark:bg-slate-800/30'
                        } ${isHighlighted ? 'z-50 shadow-[inset_0_0_0_4px_rgba(250,204,21,1)]' : ''}`}
                      >
                        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                          <span
                            className="text-xs font-bold text-slate-900 dark:text-white sm:text-sm text-center"
                            style={{
                              userSelect: 'none',
                              WebkitUserSelect: 'none',
                              msUserSelect: 'none',
                              MozUserSelect: 'none',
                            }}
                          >
                            {square}
                          </span>
                        </div>

                        {(() => {
                          // More aggressive scaling for mobile and small grids
                          let base = 22; // px for 10x10
                          let min = 11;
                          let max = 28;
                          // If on a small screen, shrink further
                          if (typeof window !== 'undefined' && window.innerWidth < 600) {
                            base = 16;
                            min = 8;
                            max = 22;
                          }
                          const scale = Math.max(min, Math.min(max, Math.round(base * (10 / columns))));
                          const fontSize = Math.max(8, Math.round(scale * 0.52));
                          const offsetStep = Math.round(scale * 0.28);
                          return (
                            <div
                              className="pointer-events-none absolute bottom-1 right-1 z-40"
                              style={{ height: scale, width: scale }}
                            >
                              {squarePlayers.map((socketId, tokenIndex) => {
                                const player = findPlayerById(socketId);
                                const playerIndex = gamePlayers.findIndex((entry) => entry.socketId === socketId);
                                const color = getPlayerColor(player, playerIndex >= 0 ? playerIndex : tokenIndex);
                                const active = gameState.currentPlayer === socketId;
                                const isWinner = Array.isArray(gameState.winners) && gameState.winners.includes(socketId);
                                // Center if only one counter, offset if multiple
                                const isSingle = squarePlayers.length === 1;
                                const offset = isSingle ? 0 : tokenIndex * offsetStep;
                                return (
                                  <div
                                    key={`${square}-${socketId}`}
                                    className={`absolute bottom-0 right-0 inline-flex items-center justify-center rounded-full font-black text-white shadow-lg ring-1 ring-white/80 transition-transform duration-150 dark:ring-slate-900 ${isWinner ? 'scale-110' : ''} ${active ? 'snl-counter-glow ring-2 ring-emerald-300/90 dark:ring-emerald-400/80' : ''}`}
                                    style={{
                                      backgroundColor: color,
                                      transform: isSingle
                                        ? 'translate(0, 0)'
                                        : `translate(${-offset}px, ${-offset}px)`,
                                      zIndex: 100 + tokenIndex,
                                      width: scale,
                                      height: scale,
                                      fontSize,
                                    }}
                                    title={player?.username || socketId}
                                  >
                                    <span className="leading-none" style={{ fontSize }}>{getPlayerLabel(player, playerIndex >= 0 ? playerIndex : tokenIndex)}</span>
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })()}

                      </div>
                    );
                  })
                )}
              </div>

              {heldCounter && typeof heldCounter.x === 'number' && typeof heldCounter.y === 'number' ? (
                <div className="pointer-events-none absolute inset-0 z-50">
                  <div
                    className={`absolute inline-flex items-center justify-center rounded-full font-black text-white shadow-lg ring-1 ring-white/80 dark:ring-slate-900${gameState.currentPlayer === heldCounter.playerId ? ' snl-counter-glow ring-2 ring-emerald-300/90 dark:ring-emerald-400/80' : ''}`}
                    style={{
                      backgroundColor: animatedCounterColor,
                      left: `${(heldCounter.x / (columns * 100)) * 100}%`,
                      top: `${(heldCounter.y / (rows * 100)) * 100}%`,
                      width: '1.7rem',
                      height: '1.7rem',
                      fontSize: '0.8rem',
                      transform: 'translate(-50%, -50%)',
                    }}
                  >
                    <span className="leading-none">{animatedCounterLabel}</span>
                  </div>
                </div>
              ) : null}
            </div>
          </section>


          <aside className="flex flex-col gap-4">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div className="flex w-full justify-center">
                {isMyTurn || gameState.finished ? (
                  <DiceFace
                    value={isRolling ? rollingFace : (typeof gameState.lastRoll === 'number' ? Number(gameState.lastRoll) : 1)}
                    rolling={isRolling}
                    onClick={handleRoll}
                    canRoll={!isRolling && !isAnimatingMove && !hasRolled && !gameState.finished && !buttonHidden}
                  />
                ) : (
                  <DiceFace
                    value={isRolling ? rollingFace : (typeof gameState.lastRoll === 'number' ? Number(gameState.lastRoll) : 1)}
                    rolling={isRolling}
                  />
                )}
              </div>
              
            </div>
            
            <div className="rounded-[1.5rem] border border-slate-200 bg-white/80 p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900/80">
              <div className="text-xs font-semibold uppercase tracking-[0.25em] text-sky-600 dark:text-sky-300">Leaderboard</div>
              
              
              <div className="mt-3 space-y-2">
                {gamePlayers.map((player, index) => {
                  const playerSquare = positions[player.socketId] || 0;
                  const playerScore = scores[player.socketId] || 0;
                  const color = getPlayerColor(player, index);
                  const active = gameState.currentPlayer === player.socketId;
                  const winner = Array.isArray(gameState.winners) && gameState.winners.includes(player.socketId);
                  return (
                    <div
                      key={player.socketId}
                      className={`rounded-2xl border px-3 py-3 ${
                        winner
                          ? 'border-amber-300 bg-amber-100 dark:border-amber-600 dark:bg-amber-950/40'
                          : active
                            ? 'border-emerald-300 bg-emerald-50 dark:border-emerald-700 dark:bg-emerald-950/40'
                            : 'border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800/60'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <span
                            className="inline-flex h-7 w-7 items-center justify-center rounded-full text-sm font-black leading-none text-white sm:h-8 sm:w-8 sm:text-base"
                            style={{ backgroundColor: color }}
                          >
                            {getPlayerLabel(player, index)}
                          </span>
                          <div>
                            <div className="font-bold text-slate-900 dark:text-white">{player.username}</div>
                            <div className="text-xs uppercase tracking-[0.15em] text-slate-500 dark:text-slate-400">
                              {winner ? 'Winner' : active ? 'Taking turn' : 'Waiting'}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-black text-slate-900 dark:text-white">{playerSquare}</div>
                          <div className="text-xs uppercase tracking-[0.15em] text-slate-500 dark:text-slate-400">Position</div>
                        </div>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-4 text-xs text-slate-600 dark:text-slate-300">
                        <span>Round wins: <span className="font-bold">{playerScore}</span></span>
                        {gameState.finished && gameState.rollCounts && typeof gameState.rollCounts[player.socketId] === 'number' && (
                          <span>Rolls: <span className="font-bold">{gameState.rollCounts[player.socketId]}</span></span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* <div className="rounded-[1.5rem] border border-slate-200 bg-white/80 p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900/80">
              <div className="text-xs font-semibold uppercase tracking-[0.25em] text-amber-600 dark:text-amber-300">Recent Moves</div>
              <div className="mt-3 space-y-2 text-sm text-slate-600 dark:text-slate-300">
                {history.length ? history.map((entry, index) => (
                  <div key={`history-${index}`} className="rounded-2xl bg-slate-100 px-3 py-2 dark:bg-slate-800">
                    {entry}
                  </div>
                )) : (
                  <div className="rounded-2xl bg-slate-100 px-3 py-2 dark:bg-slate-800">Rolls will appear here once the round starts.</div>
                )}
              </div>
            </div> */}
          </aside>
        </div>
      </div>
    </div>
  );
}

SnakesAndLaddersGame.Guide = function SnakesAndLaddersGuide() {
  return (
    <div>
      <h3 className="mb-2 font-bold">How to Play Snakes and Ladders</h3>
      <ol className="ml-6 list-decimal">
        <li>Players take turns rolling one six-sided die.</li>
        <li>Your token moves forward by the amount shown on the die.</li>
        <li>Landing on a ladder sends you upward immediately.</li>
        <li>Landing on a snake sends you downward immediately.</li>
        <li>The first player to reach the final square wins the round.</li>
        <li>You need to land on the final square with an exact roll to win.</li>
      </ol>
    </div>
  );
};


// Color glow for dice and subtle counter pulse
const style = document.createElement('style');
style.innerHTML = `
/* Prevent text selection for all UI text in Snakes and Ladders */
.snl-no-select, .snl-no-select * {
  user-select: none !important;
  -webkit-user-select: none !important;
  -ms-user-select: none !important;
  -moz-user-select: none !important;
}
@keyframes snl-dice-glow-blue {
  0%, 100% { box-shadow: 0 0 0 0 #38bdf880, 0 0 0 0 #38bdf840; }
  50% { box-shadow: 0 0 0 5px #38bdf840, 0 0 0 10px #38bdf820; }
}
@keyframes snl-dice-glow-yellow {
  0%, 100% { box-shadow: 0 0 0 0 #fde04780, 0 0 0 0 #fde04740; }
  50% { box-shadow: 0 0 0 5px #fde04740, 0 0 0 10px #fde04720; }
}
.snl-dice-glow {
  animation: snl-dice-glow-blue 1.1s cubic-bezier(0.4,0,0.6,1) infinite;
}
@media (prefers-color-scheme: dark) {
  .snl-dice-glow {
    animation: snl-dice-glow-yellow 1.1s cubic-bezier(0.4,0,0.6,1) infinite;
  }
}
.dark .snl-dice-glow {
  animation: snl-dice-glow-yellow 1.1s cubic-bezier(0.4,0,0.6,1) infinite !important;
}
@keyframes snl-counter-glow-blue {
  0%, 100% { box-shadow: 0 0 0 0 #38bdf8FF; }
  50% { box-shadow: 0 0 0 4px #38bdf8FF; }
}
@keyframes snl-counter-glow-yellow {
  0%, 100% { box-shadow: 0 0 0 0 #fde047FF; }
  50% { box-shadow: 0 0 0 4px #fde047FF; }
}
.snl-counter-glow {
  animation: snl-counter-glow-blue 1.1s cubic-bezier(0.4,0,0.6,1) infinite;
}
@media (prefers-color-scheme: dark) {
  .snl-counter-glow {
    animation: snl-counter-glow-yellow 1.1s cubic-bezier(0.4,0,0.6,1) infinite;
  }
}
.dark .snl-counter-glow {
  animation: snl-counter-glow-yellow 1.1s cubic-bezier(0.4,0,0.6,1) infinite !important;
}
`;
if (typeof document !== 'undefined' && !document.getElementById('snl-blink-style')) {
  style.id = 'snl-blink-style';
  document.head.appendChild(style);
}

// Wrap the main component in a div with the snl-no-select class
const SnakesAndLaddersGameNoSelect = (props) => (
  <div className="snl-no-select">
    <SnakesAndLaddersGame {...props} />
  </div>
);

export default SnakesAndLaddersGameNoSelect;